import { frameId } from "../assets/manifest.ts";
import { presenceCells } from "../compile/behaviour.ts";
import type { FrameDimensions } from "../compile/context.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropInstance } from "../document/types.ts";
import type { StandInDefinition } from "./adapter.ts";
import { presenceArtCells } from "./presence.ts";
import { inPresenceArea, presenceArea, type PresenceArea } from "./presence-area.ts";
import type { FrameSize } from "./prop-hit.ts";
import type { FeetPoint } from "./stand-in.ts";

/**
 * The stand-in driving presence props (ADR-071 decision 5): while it is shown, a presence prop shows its
 * inside state's frame while the stand-in's feet box lies in its area and its own frame otherwise, the answer
 * the game's `PresenceProps` gives its local character, cut rather than played. Each prop's area is the one
 * the game resolves: its painted cells as the compile writes them (`presenceCells`, mirrored on `flipX`
 * alone) or, with none, the ground under its art (`presenceArtCells`, mirrored on both flips), placed from
 * its anchor. The answers are read lazily from the state previews: the areas are resolved again only when
 * the map document is a different object (the knob for how often), and tested once each only when the feet
 * point differs from the one last tested, so a still stand-in, a redraw or a panel's render tests nothing.
 * View state: nothing here is saved, compiled or undone.
 */

/**
 * What the driver reads from its host, each read when an answer is asked for. `frameSize` is a frame's size
 * as the compile context holds it (`frameSizeOf`), which the compile mirrors painted cells by.
 */
export interface StandInPresenceHost {
  readonly map: () => MapDocument;
  readonly feet: () => FeetPoint | null;
  readonly standIn: () => StandInDefinition | null;
  readonly tileSize: () => number;
  readonly frameSize: (id: string) => FrameDimensions | undefined;
  readonly insideOf: (prop: PropInstance) => string | null;
}

/**
 * A frame's size, base and art bounds by frame id, undefined for a frame no pack provides.
 */
export type FrameRectOf = (id: string) => FrameSize | undefined;

/**
 * A presence prop's area as the game resolves it.
 *
 * @param prop The placed prop.
 * @param rect Its saved frame's size, base and art bounds from the asset manifest, undefined when no pack
 *   provides the frame; the default area is measured from it.
 * @param size Its saved frame's size in the compile context, which painted cells are mirrored by; undefined
 *   when the context cannot size it.
 * @param tileSize Tile size in pixels.
 * @returns The area, or null when the frame cannot be sized, or when the prop is mirrored with painted cells
 *   the context cannot size, which the compile refuses.
 */
export function propPresenceArea(prop: PropInstance, rect: FrameSize | undefined, size: FrameDimensions | undefined, tileSize: number): PresenceArea | null {
  const painted = prop.behaviour?.activation ?? [];
  if (rect === undefined || (prop.flipX && painted.length > 0 && size === undefined)) {
    return null;
  }
  const cells = presenceCells(prop, { frameId: frameId(prop.frame.tileset, null, prop.frame.tile), size }, tileSize);
  return presenceArea(prop, cells.length > 0 ? cells : presenceArtCells(rect, tileSize / COLLISION_CELLS_PER_TILE, prop.flipX, prop.flipY));
}

/**
 * The key a prop's answer is kept under.
 *
 * @param layerId Prop layer id.
 * @param propId Prop id.
 * @returns The key.
 */
function keyOf(layerId: string, propId: string): string {
  return `${layerId}\u0000${propId}`;
}

/**
 * The stand-in's answers for every presence prop of the edited map.
 */
export class StandInPresence {
  private rectOf: FrameRectOf | null = null;
  private resolvedFor: MapDocument | null = null;
  private readonly areas = new Map<string, PresenceArea | null>();
  private readonly inside = new Map<string, boolean>();
  private testedAt: FeetPoint | null = null;
  private resolveCount = 0;
  private testCount = 0;

  /**
   * @param host The map, the feet point, the registered stand-in, the tile size and the presence types.
   */
  public constructor(private readonly host: StandInPresenceHost) {}

  /**
   * How many times the areas have been resolved.
   *
   * @returns The count.
   */
  public get resolutions(): number {
    return this.resolveCount;
  }

  /**
   * How many area tests have run: one per presence prop each time the stand-in stood somewhere new.
   *
   * @returns The count.
   */
  public get tests(): number {
    return this.testCount;
  }

  /**
   * Gives the driver the frames' sizes, base and art bounds, which only the host's asset manifest holds; it
   * drives nothing until then. Binding again resolves every area again.
   *
   * @param rectOf The lookup, or null to drive nothing.
   */
  public bindFrames(rectOf: FrameRectOf | null): void {
    this.rectOf = rectOf;
    this.resolvedFor = null;
  }

  /**
   * Whether the stand-in stands inside a prop's area.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns True inside and false outside or when its area cannot be resolved, or null when the stand-in
   *   is not shown, no stand-in is registered, the frames are unbound, or no presence type drives the prop.
   */
  public answer(layerId: string, propId: string): boolean | null {
    const feet = this.host.feet();
    const standIn = this.host.standIn();
    if (feet === null || standIn === null || this.rectOf === null) {
      return null;
    }
    const map = this.host.map();
    if (map !== this.resolvedFor) {
      this.resolve(map, this.rectOf);
    }
    if (this.testedAt === null || this.testedAt.x !== feet.x || this.testedAt.y !== feet.y) {
      this.test(feet, standIn);
    }
    return this.inside.get(keyOf(layerId, propId)) ?? null;
  }

  /**
   * Resolves the area of every presence prop of a map, and forgets where the stand-in was last tested.
   *
   * @param map The map.
   * @param rectOf The frames' sizes.
   */
  private resolve(map: MapDocument, rectOf: FrameRectOf): void {
    const tileSize = this.host.tileSize();
    this.areas.clear();
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        if (this.host.insideOf(prop) !== null) {
          const id = frameId(prop.frame.tileset, null, prop.frame.tile);
          this.areas.set(keyOf(layer.id, prop.id), propPresenceArea(prop, rectOf(id), this.host.frameSize(id), tileSize));
        }
      }
    }
    this.resolvedFor = map;
    this.testedAt = null;
    this.resolveCount += 1;
  }

  /**
   * Tests every resolved area once for a feet point, in pixels with the registered feet box.
   *
   * @param feet The feet point.
   * @param standIn The registered stand-in.
   */
  private test(feet: FeetPoint, standIn: StandInDefinition): void {
    const box = { halfWidth: standIn.feetBox.halfWidth, height: standIn.feetBox.height, cell: this.host.tileSize() / COLLISION_CELLS_PER_TILE };
    this.inside.clear();
    for (const [key, area] of this.areas) {
      this.inside.set(key, area !== null && inPresenceArea(area, feet.x, feet.y, box));
      this.testCount += 1;
    }
    this.testedAt = { x: feet.x, y: feet.y };
  }
}
